import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useEditorStore } from '../store/editor'
import { getScene } from '../scenes'

function fmt(s: number): string {
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${m}:${sec.toString().padStart(2, '0')}`
}

export default function Playback() {
  const { t } = useTranslation()
  const currentTime = useEditorStore((s) => s.currentTime)
  const duration = useEditorStore((s) => s.duration)
  const isPlaying = useEditorStore((s) => s.isPlaying)
  const setTime = useEditorStore((s) => s.setTime)
  const togglePlay = useEditorStore((s) => s.togglePlay)
  // Remotion 场景由 Player 持有时钟，此 rAF 不参与（避免双时钟冲突）
  const sceneId = useEditorStore((s) => s.sceneId)
  const hasComposition = !!(sceneId && getScene(sceneId)?.Composition)

  // 用 ref 镜像 currentTime，避免 rAF 闭包捕获旧值、也避免每次 tick 重订阅。
  const timeRef = useRef(currentTime)
  useEffect(() => {
    timeRef.current = currentTime
  }, [currentTime])

  // 播放循环（rAF，真实 dt；到末尾循环回 0）。Remotion 场景跳过（Player 自驱）。
  useEffect(() => {
    if (!isPlaying || hasComposition) return
    let raf = 0
    let last = performance.now()
    const loop = (now: number) => {
      const dt = (now - last) / 1000
      last = now
      const next = timeRef.current + dt
      if (next >= duration) {
        timeRef.current = 0
        setTime(0)
      } else {
        timeRef.current = next
        setTime(next)
      }
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [isPlaying, duration, setTime, hasComposition])

  // Space 播放/暂停（输入控件内不触发）
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return
      const el = e.target as HTMLElement
      const tag = el.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable) return
      e.preventDefault()
      togglePlay()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [togglePlay])

  return (
    <div className="playback">
      <button className="btn btn-outline btn-sm play-btn" onClick={togglePlay} title={isPlaying ? t('common.pause') : t('common.play')}>
        {isPlaying ? '❚❚' : '▶'}
      </button>
      <span className="play-time">{fmt(currentTime)}</span>
      <input
        className="play-scrub"
        type="range"
        min={0}
        max={duration}
        step={0.1}
        value={currentTime}
        onChange={(e) => setTime(Math.min(Math.max(+e.target.value, 0), duration))}
      />
      <span className="play-time play-time-dim">{fmt(duration)}</span>
    </div>
  )
}
